import React from 'react';
import { useSelector } from 'react-redux';
import dompurify from 'dompurify';
import { Link } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Types
import { FeedbackRecognitionType } from 'Shared/constants/types';

// Helpers
import { sanitizeFullName } from 'Shared/helpers';

// Utils
import { dateMMMDDYYYY } from 'Shared/utils/DateTime.utils';
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';

// Selecotors
import * as UsersSelectors from 'Shared/selectors/Users.selectors';
import { getWorkspaceByUuid, getWorkspaceTitle, getWorkspaceTeammate } from 'Shared/selectors/workspace';

// Components
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import WpAvatar from 'Wpds/components/avatar/WpAvatar';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';
import VideoPlayer from 'Shared/components/VideoPlayer/VideoPlayer';
import Reactions from 'Shared/components/Reactions/Reactions';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Styles
import './KudoItem.scss';

type Props = {
  feedback: FeedbackRecognitionType;
};

const KudoItem = ({ feedback }: Props) => {
  const { reactionsKudos: showReactions } = useFlags();
  const { user: currentUser } = useCurrentUser();
  const creator = useSelector(state => UsersSelectors.getUserByUserId(state, { userId: feedback.creatorId }))?.toJS();
  const receiver = useSelector(state => UsersSelectors.getUserByUserId(state, { userId: feedback.receiverId }))?.toJS();
  const workspace = useSelector(state => getWorkspaceByUuid(feedback.workspaceUuid)(state)) || [];

  if (!creator || !receiver || !workspace) return null;
  const creatorLabel = currentUser.id === creator.id ? 'You' : creator.displayName;
  const receiverLabel = currentUser.id === receiver.id ? 'You' : receiver.displayName;

  const createdAt = dateMMMDDYYYY(feedback.createdAt);

  const hasText = !!feedback.likeThatText;
  const hasVideo = !!feedback.likeThatVideo;
  const hasGif = !!feedback.gifUrl;

  const baseClassName = 'KudoItem';

  const VideoMarkup = () => {
    return (
      <VideoPlayer
        className={`${baseClassName}__body__attachments__video`}
        videoID={feedback.likeThatVideo.id}
        sources={[feedback.likeThatVideo.mp4Url, feedback.likeThatVideo.url]}
        previewImageURL={feedback.likeThatVideo.previewImageUrl}
        transcription={feedback.likeThatVideo.transcription}
        audioOnly={feedback.likeThatVideo.audioOnly}
        withoutTranscription={true}
      />
    );
  };

  const AttachmentsMarkup = () => {
    if (hasGif && hasVideo) {
      return (
        <div className={`${baseClassName}__body__attachments__container`}>
          <VideoMarkup />
          <img src={feedback.gifUrl} className={`${baseClassName}__body__attachments__attachment`} />
        </div>
      );
    }

    if (hasGif) {
      return <img src={feedback.gifUrl} className={`${baseClassName}__body__attachments__attachment`} />;
    }

    if (hasVideo) {
      return (
        <div>
          <VideoMarkup />
        </div>
      );
    }

    return null;
  };

  const WorkspaceNameMarkup = () => {
    const workspaceTitle = useSelector(getWorkspaceTitle({ workspaceId: workspace.id }));
    const displayAsPublic = feedback.isPublic;
    const displayWorkspaceTitle = displayAsPublic ? 'Public' : workspaceTitle;
    const teammate = useSelector(getWorkspaceTeammate(workspace.id));
    if (!workspace) return null;

    const IconMarkup = () => {
      let icon;

      if (workspace.isTeamSync || displayAsPublic) {
        icon = <WpIcon size="sm" name="globe-americas" />;
      } else if (workspace.isGroup) {
        icon = <WpIcon size="sm" name="user-friends" />;
      } else if (workspace.isOneOnOne) {
        icon = <WpIcon size="sm" name="user" />;
      }

      if (icon) {
        return <span className={`${baseClassName}__header__title__workspace-name__icon`}>{icon}</span>;
      } else return null;
    };

    const tooltipCopy = () => {
      if (workspace.isTeamSync || workspace.isGroup) {
        return `Visible to all members of the ${workspaceTitle} workspace`;
      } else if (displayAsPublic) {
        return 'Visible to all members of your organization';
      } else if (workspace.isOneOnOne) {
        return `Visible only to you and ${sanitizeFullName(teammate.fullName)}`;
      }
    };

    return (
      <WpTooltip>
        <WpTooltipTrigger>
          <Link
            to={displayAsPublic ? '#' : PageUrlsUtils.makeWorkspaceUrl(workspace.id)}
            className={`${baseClassName}__header__title__workspace-name`}
          >
            <IconMarkup />
            <span className={`${baseClassName}__header__title__workspace-name__name`}>{displayWorkspaceTitle}</span>
          </Link>
        </WpTooltipTrigger>
        <WpTooltipContent>{tooltipCopy()}</WpTooltipContent>
      </WpTooltip>
    );
  };

  return (
    <WpContainer className={baseClassName}>
      <WpContainerBody className={`${baseClassName}__container`}>
        <div className={`${baseClassName}__header`}>
          <WpAvatar avatar={creator.avatar} size="lg" />
          <div className={`${baseClassName}__header__title`}>
            <p className={`${baseClassName}__header__names`}>
              <span className={`${baseClassName}__header__names__name`}>{creatorLabel}</span>
              <span className={`${baseClassName}__header__names__seperator`}>gave kudos to</span>
              <span className={`${baseClassName}__header__names__name`}>{receiverLabel}</span>
            </p>
            <div className={`${baseClassName}__header__title__wrapper`}>
              <WorkspaceNameMarkup />
              <p className={`${baseClassName}__header__title__time`}>{createdAt}</p>
            </div>
          </div>
        </div>
        <div className={`${baseClassName}__body`}>
          {(hasGif || hasVideo) && (
            <div className={`${baseClassName}__body__attachments`}>
              <AttachmentsMarkup />
            </div>
          )}
          {hasText && (
            <p className={`${baseClassName}__body__text`}>
              <span>{dompurify.sanitize(feedback.likeThatText)}</span>
            </p>
          )}
        </div>
        {showReactions && (
          <div className={`${baseClassName}__reactions`}>
            <Reactions reactionableType="feedback" reactionableId={feedback.uuid} />
          </div>
        )}
      </WpContainerBody>
    </WpContainer>
  );
};

export default KudoItem;
